import React, { useEffect, useState } from "react";
import { db } from "../firebase";

import {
  collection,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";

import { useNavigate } from "react-router-dom";

import {
  FaUsers,
  FaUserPlus,
  FaUser,
  FaClock,
  FaCheckCircle,
  FaChevronRight,
  FaSyncAlt,
  FaArrowLeft,
} from "react-icons/fa";

import "./NewRegister.css";


/* =========================================================
   NEW REGISTER PAGE
========================================================= */

const NewRegister = () => {

  const navigate = useNavigate();


  /* =======================================================
     STATE
  ======================================================= */

  const [levels, setLevels] = useState({
    1: [],
    2: [],
    3: [],
  });

  const [activeLevel, setActiveLevel] = useState(1);

  const [loading, setLoading] = useState(true);

  const [refreshing, setRefreshing] = useState(false);

  const [error, setError] = useState("");


  const loggedInUsername =
    localStorage.getItem("username");


  /* =======================================================
     CHECK IF DATE IS TODAY
  ======================================================= */

  const isToday = (timestamp) => {

    if (!timestamp) {
      return false;
    }

    try {

      let date;

      /*
       * Firestore Timestamp
       */
      if (timestamp?.seconds) {

        date = new Date(
          timestamp.seconds * 1000
        );

      }

      /*
       * JavaScript Date
       */
      else if (timestamp instanceof Date) {

        date = timestamp;

      }

      /*
       * String / number
       */
      else {

        date = new Date(timestamp);

      }


      if (
        Number.isNaN(
          date.getTime()
        )
      ) {

        return false;

      }


      const today = new Date();


      return (
        date.getFullYear() ===
          today.getFullYear() &&

        date.getMonth() ===
          today.getMonth() &&

        date.getDate() ===
          today.getDate()
      );

    } catch (err) {

      console.error(
        "Date checking error:",
        err
      );

      return false;

    }

  };


  /* =======================================================
     FORMAT TIME
  ======================================================= */

  const formatTime = (timestamp) => {

    if (!timestamp) {
      return "Today";
    }

    try {

      let date;

      if (timestamp?.seconds) {

        date = new Date(
          timestamp.seconds * 1000
        );

      } else if (
        timestamp instanceof Date
      ) {

        date = timestamp;

      } else {

        date = new Date(timestamp);

      }


      if (
        Number.isNaN(
          date.getTime()
        )
      ) {

        return "Today";

      }


      return date.toLocaleTimeString(
        [],
        {
          hour: "2-digit",
          minute: "2-digit",
        }
      );

    } catch {

      return "Today";

    }

  };


  /* =======================================================
     FORMAT DATE
  ======================================================= */

  const formatDate = (timestamp) => {

    if (!timestamp) {
      return "Today";
    }

    try {

      let date;

      if (timestamp?.seconds) {

        date = new Date(
          timestamp.seconds * 1000
        );

      } else {

        date = new Date(timestamp);

      }


      if (
        Number.isNaN(
          date.getTime()
        )
      ) {

        return "Today";

      }


      return date.toLocaleDateString(
        [],
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );

    } catch {

      return "Today";

    }

  };


  /* =======================================================
     FETCH LEVEL USERS
  ======================================================= */

  const fetchLevelUsers = async (
    referralCodes,
    maxLevel = 3
  ) => {

    const levelsData = {
      1: [],
      2: [],
      3: [],
    };


    let level = 1;

    let currentCodes = [
      ...new Set(
        referralCodes.filter(Boolean)
      ),
    ];


    /*
     * Continue until Level 3
     */

    while (
      level <= maxLevel &&
      currentCodes.length > 0
    ) {

      /*
       * Firestore "in" query supports
       * maximum 10 values.
       */

      const chunks = [];

      for (
        let i = 0;
        i < currentCodes.length;
        i += 10
      ) {

        chunks.push(
          currentCodes.slice(
            i,
            i + 10
          )
        );

      }


      let users = [];


      /*
       * Query all chunks in parallel
       */

      const snapshots =
        await Promise.all(

          chunks.map(
            async (chunk) => {

              const usersQuery =
                query(
                  collection(
                    db,
                    "users"
                  ),

                  where(
                    "whoReferredWho",
                    "in",
                    chunk
                  )
                );


              return getDocs(
                usersQuery
              );

            }
          )

        );


      /*
       * Collect users
       */

      snapshots.forEach(
        (snap) => {

          snap.forEach(
            (doc) => {

              const data =
                doc.data();


              /*
               * Only today's registrations
               */

              if (
                isToday(
                  data.createdAt
                )
              ) {

                users.push({

                  id: doc.id,

                  username:
                    data.username ||
                    "Unknown",

                  referralCode:
                    data.referralCode ||
                    "",

                  createdAt:
                    data.createdAt,

                });

              }

            }
          );

        }
      );


      /*
       * Remove duplicates
       */

      const uniqueUsers =
        Array.from(
          new Map(
            users.map(
              (user) => [
                user.id,
                user,
              ]
            )
          ).values()
        );


      /*
       * Save current level
       */

      levelsData[level] =
        uniqueUsers;


      /*
       * Find referral codes
       * for next level
       */

      currentCodes =
        uniqueUsers
          .map(
            (user) =>
              user.referralCode
          )
          .filter(Boolean);


      level++;

    }


    /*
     * Always make sure all
     * levels exist.
     */

    levelsData[1] =
      levelsData[1] || [];

    levelsData[2] =
      levelsData[2] || [];

    levelsData[3] =
      levelsData[3] || [];


    return levelsData;

  };


  /* =======================================================
     LOAD CURRENT USER
  ======================================================= */

  const fetchCurrentUser = async () => {

    if (!loggedInUsername) {
      return null;
    }


    const q = query(
      collection(
        db,
        "users"
      ),

      where(
        "username",
        "==",
        loggedInUsername
      ),

      limit(1)
    );


    const snap =
      await getDocs(q);


    if (snap.empty) {
      return null;
    }


    return {
      id: snap.docs[0].id,
      ...snap.docs[0].data(),
    };

  };


  /* =======================================================
     LOAD REGISTERS
  ======================================================= */

  const loadRegisters = async (
    showRefresh = false
  ) => {

    if (!loggedInUsername) {

      setLoading(false);

      return;

    }


    if (showRefresh) {

      setRefreshing(true);

    }


    setError("");


    try {

      const user =
        await fetchCurrentUser();


      /*
       * User not found
       */

      if (!user) {

        setLevels({
          1: [],
          2: [],
          3: [],
        });

        setError(
          "Your account could not be found."
        );

        return;

      }


      /*
       * User has no referral code
       */

      if (!user.referralCode) {

        setLevels({
          1: [],
          2: [],
          3: [],
        });

        return;

      }


      /*
       * Fetch today's
       * registrations
       */

      const data =
        await fetchLevelUsers(
          [user.referralCode],
          3
        );


      setLevels(data);

    } catch (error) {

      console.error(
        "Failed loading new registers:",
        error
      );


      setError(
        "Unable to load new registrations."
      );

    } finally {

      setLoading(false);

      setRefreshing(false);

    }

  };


  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {

    loadRegisters();

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [loggedInUsername]);


  /* =======================================================
     TOTAL MEMBERS TODAY
  ======================================================= */

  const totalToday =
    (levels[1]?.length || 0) +
    (levels[2]?.length || 0) +
    (levels[3]?.length || 0);


  /* =======================================================
     CURRENT LEVEL USERS
  ======================================================= */

  const currentUsers =
    levels[activeLevel] || [];


  /* =======================================================
     LEVEL TOTAL
  ======================================================= */

  const currentLevelCount =
    currentUsers.length;


  /* =======================================================
     LOGIN REQUIRED
  ======================================================= */

  if (!loggedInUsername) {

    return (

      <div className="register-wrapper">

        <div className="register-empty">

          <div className="register-empty-icon">

            <FaUser />

          </div>

          <h3>
            Login Required
          </h3>

          <p>
            Please log in first to
            view your new members.
          </p>

          <button
            type="button"
            className="register-back-empty"
            onClick={() =>
              navigate(-1)
            }
          >
            <FaArrowLeft />

            Back
          </button>

        </div>

      </div>

    );

  }


  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {

    return (

      <div className="register-wrapper">

        <div className="register-loading">

          <div className="register-spinner" />

          <strong>
            Loading registrations
          </strong>

          <span>
            Checking your new members...
          </span>

        </div>

      </div>

    );

  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (

    <div className="register-wrapper">


      {/* =================================================
          TOP HEADER
      ================================================= */}

      <div className="register-header">


        {/* LEFT SIDE */}

        <div className="register-header-left">


          {/* BACK */}

          <button
            type="button"
            className="register-back-btn"
            onClick={() =>
              navigate(-1)
            }
            title="Go back"
          >

            <FaArrowLeft />

            <span>
              Back
            </span>

          </button>


          {/* TITLE */}

          <div className="register-title">

            <div className="register-title-icon">

              <FaUserPlus />

            </div>


            <div>

              <span>
                TEAM ACTIVITY
              </span>

              <h1>
                New Registers
              </h1>

            </div>

          </div>


        </div>


        {/* REFRESH */}

        <button
          type="button"
          className={
            refreshing
              ? "register-refresh spinning"
              : "register-refresh"
          }
          onClick={() =>
            loadRegisters(true)
          }
          title="Refresh registrations"
        >

          <FaSyncAlt />

        </button>


      </div>


      {/* =================================================
          ERROR
      ================================================= */}

      {error && (

        <div className="register-error">

          <span>
            {error}
          </span>

          <button
            type="button"
            onClick={() =>
              loadRegisters(true)
            }
          >
            Try Again
          </button>

        </div>

      )}


      {/* =================================================
          SUMMARY
      ================================================= */}

      <div className="register-summary">


        <div className="summary-icon">

          <FaUsers />

        </div>


        <div className="summary-text">

          <span>
            NEW MEMBERS
          </span>

          <strong>
            {totalToday}
          </strong>

          <small>
            Registered today
          </small>

        </div>


        <div className="summary-status">

          <FaCheckCircle />

          <span>
            TODAY
          </span>

        </div>


      </div>


      {/* =================================================
          LEVEL TABS
      ================================================= */}

      <div className="register-level-tabs">


        {[1, 2, 3].map(
          (level) => {

            const count =
              levels[level]?.length ||
              0;


            return (

              <button
                key={level}
                type="button"
                className={
                  activeLevel === level
                    ? "register-level-tab active"
                    : "register-level-tab"
                }
                onClick={() => {

                  setActiveLevel(
                    level
                  );

                }}
              >

                <span>
                  LEVEL {level}
                </span>

                <strong>
                  {count}
                </strong>

              </button>

            );

          }
        )}


      </div>


      {/* =================================================
          ACTIVE LEVEL INFO
      ================================================= */}

      <div className="register-section-head">


        <div>

          <span>
            TODAY'S REGISTRATIONS
          </span>

          <strong>
            Level {activeLevel}
          </strong>

        </div>


        <div className="register-count">

          {currentLevelCount}

          <span>
            members
          </span>

        </div>


      </div>


      {/* =================================================
          USER LIST
      ================================================= */}

      {currentUsers.length > 0 ? (

        <div className="register-list">


          {currentUsers.map(
            (user, index) => (

              <div
                key={user.id}
                className="register-row"
              >


                {/* NUMBER */}

                <div className="register-number">

                  {String(
                    index + 1
                  ).padStart(2, "0")}

                </div>


                {/* AVATAR */}

                <div className="register-avatar">

                  <FaUser />

                </div>


                {/* USER INFO */}

                <div className="register-user">

                  <strong>

                    {user.username ||
                      "Unknown"}

                  </strong>


                  <div className="register-meta">


                    <span>

                      <FaClock />

                      {formatTime(
                        user.createdAt
                      )}

                    </span>


                    <span>

                      Level{" "}
                      {activeLevel}

                    </span>


                  </div>

                </div>


                {/* RIGHT SIDE */}

                <div className="register-right">


                  <span className="new-badge">

                    NEW

                  </span>


                  <FaChevronRight />


                </div>


              </div>

            )
          )}


        </div>

      ) : (


        /* =================================================
           EMPTY
        ================================================= */

        <div className="register-empty">


          <div className="register-empty-icon">

            <FaUserPlus />

          </div>


          <h3>
            No New Members
          </h3>


          <p>

            There are no new members
            in Level {activeLevel} today.

          </p>


          <div className="empty-date">

            <FaClock />

            <span>

              Checked today

            </span>

          </div>


        </div>

      )}


      {/* =================================================
          FOOTER
      ================================================= */}

      <div className="register-footer">

        <span>
          ANT BET AGENCY
        </span>

        <small>
          Team registration activity
        </small>

        <strong>
          {formatDate(new Date())}
        </strong>

      </div>


    </div>

  );

};


export default NewRegister;
